// Check for async iteration support
if (typeof globalThis.ReadableStream?.prototype[Symbol.asyncIterator] !== "function") {
  // Safari does not support asynciterable on ReadableStream yet, otherwise we would not need it.
  // @see https://bugs.webkit.org/show_bug.cgi?id=194379
  // @see https://developer.mozilla.org/en-US/docs/Web/API/ReadableStream#browser_compatibility
  await import("web-streams-polyfill/polyfill");
}

const concatUint8Arrays = async (uint8arrays: Uint8Array<ArrayBuffer>[]) => {
  const blob = new Blob(uint8arrays);
  const buffer = await blob.arrayBuffer();
  return new Uint8Array(buffer);
};

const streamToUint8Array = async (stream: ReadableStream<Uint8Array<ArrayBuffer>>) => {
  // Read all the bytes from this stream.
  const chunks: Uint8Array<ArrayBuffer>[] = [];
  for await (const chunk of stream) {
    chunks.push(chunk);
  }
  return concatUint8Arrays(chunks);
};

export const compressToUint8Array = async (
  source: string | Uint8Array<ArrayBuffer>[],
  options?: { signal?: AbortSignal },
) => {
  const stream = new Blob(typeof source === "string" ? [source] : source).stream();
  const compressedStream = stream.pipeThrough(new CompressionStream("gzip"), { signal: options?.signal });
  return await streamToUint8Array(compressedStream);
};

export const compressStringToBase64 = async (source: string, options?: { signal?: AbortSignal }) => {
  const compressedData = await compressToUint8Array(source, options);
  return (
    compressedData
      // @ts-expect-error type def should be added in some of the next ts versions
      // its widely available in browsers already though
      .toBase64()
  );
};

export const decompressFromUint8Array = async (
  compressedBytes: Uint8Array<ArrayBuffer>,
  options?: { signal?: AbortSignal },
) => {
  const stream = new Blob([compressedBytes]).stream();
  const decompressedStream = stream.pipeThrough(new DecompressionStream("gzip"), { signal: options?.signal });
  const stringBytes = await streamToUint8Array(decompressedStream);

  // As our input for compression is utf-8 we have to convert the bytes
  // to a uft-8 text again, which TextDecoder does by default
  return new TextDecoder().decode(stringBytes);
};

export const decompressFromBase64 = async (compressedBase64: string, options?: { signal?: AbortSignal }) => {
  const bytes = Uint8Array
    // @ts-expect-error type def should be added in some of the next ts versions
    // its widely available in browsers already though
    .fromBase64(compressedBase64);
  return await decompressFromUint8Array(bytes, options);
};
